import { faMagnifyingGlass, faSliders } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';

import { Link } from 'react-router-dom';
import useIsInViewport from '../../hooks/isInViewport';
import { getMovies } from '../../api';
import { getGenres } from '../../api/genres';
import ImageLoader from '../../common/ImageLoader';
import ResponsiveContainer from '../../common/ResponsiveContainer';
import './Movies.css';

const Movies = () => {
  const [movies, setMovies] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [maxPage, setMaxPage] = useState(1);
  const [enableAutoFetch, setEnableAutoFetch] = useState(false);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  const isEndVisible = useIsInViewport(ref);

  const placeholder = [...Array(20).keys()];

  useEffect(() => {
    getGenres().then((res) => setGenres(res.data));
  }, []);

  const applySearch = (value) => {
    const trimmed = value.trim();
    if (trimmed !== query) {
      setQuery(trimmed);
      setMovies([]);
      setLoading(true);
      setPage(1);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    applySearch(search);
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => applySearch(value), 500);
  };

  const handleGenreClick = (genre) => {
    setSelectedGenre(genre);
    setMovies([]);
    setLoading(true);
    setPage(1);
  };

  useEffect(() => {
    if (!isEndVisible) return;

    if (page < maxPage) {
      setPage(page + 1);
    }
  }, [isEndVisible, enableAutoFetch]);

  useEffect(() => {
    getMovies({ limit: 20, page, search: query, genre: selectedGenre?._id ?? '' })
      .then((res) => {
        setMaxPage(res.data.maxPage);
        setMovies((prev) => [...prev, ...res.data.movies]);
        setEnableAutoFetch(true);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.status === 403 ? 'forbidden' : 'error');
        setLoading(false);
      });
  }, [page, query, selectedGenre]);

  return (
    <ResponsiveContainer className='min-h-screen bg-repeat md:px-5'>
      <div className='bg-white rounded-xl p-2 mt-2 shadow'>
        <div className='flex items-center gap-2 h-14 px-2'>
          <h1 className='text-2xl font-bold font-montserrat text-red-1 hidden md:block shrink-0 px-3'>Películas</h1>
          <form onSubmit={handleSearch} className='flex gap-2 flex-1' action=''>
            <input
              type='text'
              placeholder='Buscar...'
              onChange={handleSearchInput}
              className='transition-colors border-2 w-full rounded h-10 leading-10 focus:border-red-2 focus:outline-none pl-3 font-montserrat font-semibold text-gray-400'
            />
            <button type='submit' className='h-10 w-10 shrink-0 rounded-xl bg-gray-100 focus:bg-red-100 hover:bg-red-100 transition-colors hover:text-red-2 text-gray-400'>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </button>
          </form>
          {genres.length > 0 && (
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`relative h-10 w-10 shrink-0 rounded-xl transition-colors ${showFilters ? 'bg-red-100 text-red-1' : 'bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-2'}`}
            >
              <FontAwesomeIcon icon={faSliders} />
              {selectedGenre && (
                <span className='absolute top-1.5 right-1.5 w-2 h-2 bg-red-1 rounded-full' />
              )}
            </button>
          )}
        </div>
        {showFilters && genres.length > 0 && (
          <div className='filter-panel flex flex-wrap gap-2 px-1 pb-2'>
            <button
              onClick={() => handleGenreClick(null)}
              className={`px-3 py-1 rounded-full border text-sm font-montserrat font-medium transition-colors cursor-pointer ${
                selectedGenre === null
                  ? 'bg-red-1 text-white border-red-1'
                  : 'border-gray-200 text-gray-500 hover:border-red-1 hover:text-red-1'
              }`}
            >
              Todos
            </button>
            {genres.map((genre) => (
              <button
                key={genre._id}
                onClick={() => handleGenreClick(genre)}
                className={`px-3 py-1 rounded-full border text-sm font-montserrat font-medium transition-colors cursor-pointer ${
                  selectedGenre?._id === genre._id
                    ? 'bg-red-1 text-white border-red-1'
                    : 'border-gray-200 text-gray-500 hover:border-red-1 hover:text-red-1'
                }`}
              >
                {genre.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        {error ? (
          <div className='flex flex-col items-center justify-center min-h-[60vh] text-center gap-4'>
            <p className='text-5xl'>🔒</p>
            <h2 className='font-montserrat font-bold text-xl text-gray-800'>
              {error === 'forbidden' ? 'No tienes acceso a este contenido' : 'Error al cargar el contenido'}
            </h2>
            <p className='font-montserrat text-gray-500 text-sm'>
              {error === 'forbidden'
                ? 'Tu cuenta no tiene permisos para ver las películas.'
                : 'Ocurrió un error inesperado. Intenta de nuevo más tarde.'}
            </p>
          </div>
        ) : (
        <>
        <div className='grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 mt-5'>
          {loading
            ? placeholder.map((item, index) => (
                <div key={index} className='gallery-item bg-slate-200 animate-pulse rounded-lg overflow-hidden relative inline-block cursor-pointer'>
                  <div className='aspect-[4/6]'></div>
                </div>
              ))
            : movies.length > 0
              ? movies.map((movie, index) => (
                  <Link
                    key={index}
                    to={`/peliculas/${movie.url}`}
                    className='gallery-item bg-white rounded-lg overflow-hidden relative inline-block cursor-pointer'
                  >
                    <ImageLoader src={movie.imagen_catalogo} alt='' className='aspect-[4/6]' />
                    <div className='p-2 overlay absolute top-0 left-0 w-full h-full bg-black bg-opacity-50'>
                      <span className='font-semibold text-white text-2xl'>{movie.titulo_original}</span>
                    </div>
                  </Link>
                ))
              : null}
        </div>
        {!loading && movies.length === 0 && (
          <div className='flex flex-col items-center justify-center min-h-[40vh] text-center gap-3'>
            <p className='text-5xl'>🎬</p>
            <h2 className='font-montserrat font-bold text-lg text-gray-700'>No se encontraron películas</h2>
            <p className='font-montserrat text-gray-400 text-sm'>Intentá con otro término o quitá los filtros.</p>
          </div>
        )}
        <div ref={ref} className='h-1 w-full relative bottom-[20rem]' />
        </>
        )}
      </div>
    </ResponsiveContainer>
  );
};

export default Movies;
